import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { appStatusDraftStatusState, appStatusLiveState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import type { AppStatusDoc, AppStatusDocKind } from '@rock/types';
import type { FC } from 'react';
import { Link } from 'react-router-dom';

const TOOLS: Array<{ kind: AppStatusDocKind; label: string; to: string; blurb: string }> = [
    {
        kind: 'alert',
        label: 'Alert Banner',
        to: '/system/status/alert',
        blurb: 'Incident status message pinned over every screen, including login',
    },
    {
        kind: 'required-upgrade',
        label: 'Required Upgrade',
        to: '/system/status/upgrade',
        blurb: 'Block installs below a minimum version behind an "Update required" screen',
    },
    {
        kind: 'eas-update',
        label: 'Forced OTA Update',
        to: '/system/status/ota',
        blurb: 'Long-running sessions apply downloaded JS updates on return-to-foreground',
    },
    {
        kind: 'maintenance',
        label: 'Maintenance Mode',
        to: '/system/status/maintenance',
        blurb: 'Kill switch: full-screen "temporarily unavailable" for every user',
    },
    {
        kind: 'store-urls',
        label: 'Store Links',
        to: '/system/status/upgrade',
        blurb: 'Where the "Update App" button sends users (edited on the Required Upgrade page)',
    },
];

function liveSummary(live: AppStatusDoc | undefined): { label: string; alarming: boolean } {
    if (!live) return { label: 'Never published', alarming: false };
    if (live.object === 'app-status-store-urls') {
        return { label: live.ios?.trim() || live.android?.trim() ? 'Set' : 'Not set', alarming: false };
    }
    return live.enabled ? { label: 'ACTIVE', alarming: true } : { label: 'Off', alarming: false };
}

const ToolRow: FC<(typeof TOOLS)[number]> = ({ kind, label, to, blurb }) => {
    const live = useAsyncAtomValue(appStatusLiveState(kind), undefined);
    const draftStatus = useAsyncAtomValue(appStatusDraftStatusState(kind), undefined);
    const summary = liveSummary(live);
    const hasDraft = draftStatus === 'draft-pending' || draftStatus === 'draft-differs';

    return (
        <div className="flex items-center gap-3 border rounded-lg p-4">
            <div className="flex-1">
                <div className="font-medium">{label}</div>
                <div className="text-sm text-muted-foreground">{blurb}</div>
            </div>
            {hasDraft && (
                <Badge variant="outline" className="text-blue-700 border-blue-700">
                    Unpublished draft
                </Badge>
            )}
            <Badge variant={summary.alarming ? 'destructive' : 'secondary'}>{summary.label}</Badge>
            <Button asChild variant="outline" size="sm">
                <Link to={to}>Open</Link>
            </Button>
        </div>
    );
};

/** Mid-incident at-a-glance: what's live and what's staged, per tool. */
export const AppStatusOverviewPage: FC = () => (
    <div className="p-6 max-w-3xl space-y-4">
        <Card>
            <CardHeader>
                <CardTitle>App Status</CardTitle>
                <CardDescription>
                    Incident tools for the mobile app. Each tool stages as a draft (previewable on your device via
                    Settings → "Preview staged content") and publishes independently — updating one never touches
                    another's pending draft. Published changes reach every app within seconds.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
                {TOOLS.map((tool) => (
                    <ToolRow key={tool.kind} {...tool} />
                ))}
            </CardContent>
        </Card>
    </div>
);
